import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  FireIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  HomeIcon,
  MapPinIcon,
  ChevronDownIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/solid';

// Sport configuration with colors
const SPORT_COLORS = {
  nhl: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/30', accent: 'blue' },
  nba: { bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/30', accent: 'orange' },
  nfl: { bg: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/30', accent: 'green' },
};

/**
 * Sortable table header
 */
function SortHeader({ label, sortKey, currentSort, onSort, align = 'left', className = '' }) {
  const isActive = currentSort.key === sortKey;
  const alignClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';

  return (
    <th
      className={`py-3 px-3 text-[10px] font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-text-primary transition-colors select-none ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <div className={`flex items-center gap-1 ${alignClass}`}>
        <span>{label}</span>
        {isActive && (
          currentSort.dir === 'asc'
            ? <ArrowUpIcon className="w-3 h-3 text-accent-blue" />
            : <ArrowDownIcon className="w-3 h-3 text-accent-blue" />
        )}
      </div>
    </th>
  );
}

/**
 * Sport filter tabs
 */
function SportTabs({ selected, onChange, sportCounts }) {
  const sports = [
    { id: 'all', name: 'All' },
    { id: 'nhl', name: 'NHL' },
    { id: 'nba', name: 'NBA' },
    { id: 'nfl', name: 'NFL' },
  ];

  return (
    <div className="flex gap-1 p-1 bg-bg-secondary rounded-lg">
      {sports.map(sport => {
        const count = sport.id === 'all'
          ? Object.values(sportCounts).reduce((a, b) => a + b, 0)
          : sportCounts[sport.id] || 0;
        const isActive = selected === sport.id;
        const colors = SPORT_COLORS[sport.id];

        return (
          <button
            key={sport.id}
            onClick={() => onChange(sport.id)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
              isActive
                ? sport.id === 'all'
                  ? 'bg-accent-blue text-white'
                  : `${colors?.bg} ${colors?.text} ${colors?.border} border`
                : 'text-text-secondary hover:text-text-primary hover:bg-bg-card'
            }`}
          >
            {sport.name}
            {count > 0 && (
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[9px] ${
                isActive ? 'bg-white/20' : 'bg-bg-card'
              }`}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Streak badge - shows W5 or L3 with appropriate styling
 */
function StreakBadge({ streak, streakDisplay, isHot, isCold }) {
  if (streak === 0) {
    return <span className="text-xs text-text-secondary">-</span>;
  }

  const isWinning = streak > 0;

  if (isHot) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/25">
        <FireIcon className="w-3 h-3" />
        {streakDisplay}
      </span>
    );
  }

  if (isCold) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-blue-600 to-cyan-500 text-white">
        <ArrowTrendingDownIcon className="w-3 h-3" />
        {streakDisplay}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${
      isWinning
        ? 'bg-success/15 text-success border border-success/30'
        : 'bg-danger/15 text-danger border border-danger/30'
    }`}>
      {isWinning ? <ArrowTrendingUpIcon className="w-3 h-3" /> : <ArrowTrendingDownIcon className="w-3 h-3" />}
      {streakDisplay}
    </span>
  );
}


/**
 * Point differential indicator
 */
function PointDiff({ diff }) {
  const isPositive = diff > 0;
  const displayValue = isPositive ? `+${diff.toFixed(1)}` : diff.toFixed(1);

  return (
    <span className={`text-xs font-semibold ${
      isPositive ? 'text-success' : diff < 0 ? 'text-danger' : 'text-text-secondary'
    }`}>
      {displayValue}
    </span>
  );
}


/**
 * Format game time from various formats to readable time
 */
function formatGameTime(timeStr) {
  if (!timeStr) return '';

  // If it's already a time format like "7:00 PM"
  if (timeStr.includes('AM') || timeStr.includes('PM')) {
    return timeStr;
  }

  // If it's HH:MM:SS format (e.g., "01:20:00")
  if (timeStr.match(/^\d{2}:\d{2}:\d{2}$/)) {
    const [hours, minutes] = timeStr.split(':');
    let h = parseInt(hours, 10);
    const m = minutes;
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
  }

  // If it's ISO datetime
  if (timeStr.includes('T')) {
    try {
      const date = new Date(timeStr);
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch {
      return timeStr;
    }
  }

  return timeStr;
}

/**
 * Matchup display with logos and game info - shows AWAY @ HOME format
 */
function GameMatchupCell({ away, home, gameTime, sport, onGameClick }) {
  const formattedTime = formatGameTime(gameTime);
  const sportColor = SPORT_COLORS[sport];

  return (
    <div
      className="flex items-center gap-1.5 cursor-pointer group"
      onClick={onGameClick}
    >
      {/* Sport indicator */}
      <span className={`w-1 h-7 rounded-full ${sportColor?.bg}`}></span>

      {/* Away Team */}
      <div className="flex items-center gap-1">
        {away?.logo_url && (
          <img src={away.logo_url} alt={away.team_abbreviation} className="w-5 h-5 object-contain" />
        )}
        <div className="flex flex-col">
          <span className="font-bold text-text-primary text-xs group-hover:text-accent-blue transition-colors">
            {away?.team_abbreviation || '???'}
          </span>
          <span className="text-[9px] text-text-secondary">{away?.season_record || '0-0'}</span>
        </div>
      </div>

      {/* @ symbol */}
      <span className="text-[10px] text-text-secondary font-medium">@</span>

      {/* Home Team */}
      <div className="flex items-center gap-1">
        {home?.logo_url && (
          <img src={home.logo_url} alt={home.team_abbreviation} className="w-5 h-5 object-contain" />
        )}
        <div className="flex flex-col">
          <span className="font-bold text-text-primary text-xs group-hover:text-accent-blue transition-colors">
            {home?.team_abbreviation || '???'}
          </span>
          <span className="text-[9px] text-text-secondary">{home?.season_record || '0-0'}</span>
        </div>
      </div>

      {/* Game time */}
      {formattedTime && (
        <span className="text-[10px] text-text-secondary bg-bg-secondary px-1.5 py-0.5 rounded">
          {formattedTime}
        </span>
      )}
    </div>
  );
}

/**
 * Head to head record display
 */
function H2HDisplay({ record, lastResult, lastDate }) {
  if (!record) {
    return <span className="text-xs text-text-secondary">-</span>;
  }

  return (
    <div className="flex flex-col items-center">
      <span className="text-xs font-semibold text-text-primary">{record}</span>
      {lastResult && (
        <span className={`text-[9px] ${
          lastResult === 'W' ? 'text-success' : 'text-danger'
        }`}>
          Last: {lastResult}
        </span>
      )}
    </div>
  );
}

/**
 * Main Team Streaks Table Component
 */
export default function TeamStreaksTable({ teamsData, loading, error, onRefresh }) {
  const navigate = useNavigate();
  const [selectedSport, setSelectedSport] = useState('all');
  const [sort, setSort] = useState({ key: 'game_time', dir: 'asc' });
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Count games by sport (API now returns games, not teams)
  const sportCounts = useMemo(() => {
    const counts = { nhl: 0, nba: 0, nfl: 0 };

    if (teamsData?.sports) {
      Object.entries(teamsData.sports).forEach(([sport, data]) => {
        counts[sport] = data.games?.length || data.total_games || 0;
      });
    }

    return counts;
  }, [teamsData]);

  // Flatten games from all sports (API now returns game-centric data)
  const filteredGames = useMemo(() => {
    if (!teamsData?.sports) return [];

    let allGames = [];
    Object.entries(teamsData.sports).forEach(([sport, data]) => {
      if (data.games) {
        allGames.push(...data.games);
      }
    });

    // Filter by sport
    if (selectedSport !== 'all') {
      allGames = allGames.filter(g => g.sport === selectedSport);
    }

    // Sort by game time or by home team's stat
    allGames.sort((a, b) => {
      if (sort.key === 'game_time') {
        const aVal = a.game_time || '';
        const bVal = b.game_time || '';
        return sort.dir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      // Default sort by home team's relevant stat
      let aVal = a.home?.[sort.key] || 0;
      let bVal = b.home?.[sort.key] || 0;

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (sort.dir === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return allGames;
  }, [teamsData, selectedSport, sort]);

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleGameClick = (team) => {
    navigate(`/leagues/${team.sport}/game/${team.game_id}/analytics`);
  };

  const toggleRowExpand = (teamId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-bg-card overflow-hidden">
        <div className="p-4 border-b border-border bg-gradient-to-r from-bg-card to-bg-secondary">
          <h2 className="text-lg font-bold text-text-primary">Team Trends</h2>
          <p className="text-xs text-text-secondary">Games today with team stats</p>
        </div>
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-3 text-text-secondary">
            <ArrowPathIcon className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading team data...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-border bg-bg-card overflow-hidden">
        <div className="p-4 border-b border-border">
          <h2 className="text-lg font-bold text-text-primary">Team Trends</h2>
        </div>
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-danger text-sm mb-3">{error}</p>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="px-3 py-1.5 bg-accent-blue text-white rounded text-xs hover:bg-accent-blue/90"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  const totalGames = teamsData?.total_games || 0;

  return (
    <div className="rounded-xl border border-border bg-bg-card overflow-hidden shadow-lg">
      {/* Header */}
      <div className="p-4 border-b border-border bg-gradient-to-r from-bg-card via-bg-secondary to-bg-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-text-primary flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent-blue animate-pulse"></span>
              Team Trends
            </h2>
            <p className="text-xs text-text-secondary">
              {totalGames} {totalGames === 1 ? 'game' : 'games'} today
            </p>
          </div>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-2 rounded-lg hover:bg-bg-secondary text-text-secondary hover:text-text-primary transition-colors"
              title="Refresh"
            >
              <ArrowPathIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Sport tabs */}
        <SportTabs
          selected={selectedSport}
          onChange={setSelectedSport}
          sportCounts={sportCounts}
        />
      </div>

      {/* Table */}
      {filteredGames.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-bg-secondary flex items-center justify-center mb-4">
            <MapPinIcon className="w-8 h-8 text-text-secondary/30" />
          </div>
          <h3 className="text-base font-medium text-text-primary mb-1">No games scheduled</h3>
          <p className="text-text-secondary text-sm">
            Check back later for team matchups
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-bg-secondary/50 border-b border-border">
              <tr>
                <SortHeader label="Matchup" sortKey="game_time" currentSort={sort} onSort={handleSort} />
                <th className="py-3 px-2 text-[10px] font-semibold text-text-secondary uppercase tracking-wider text-center" colSpan={2}>Streak</th>
                <th className="py-3 px-2 text-[10px] font-semibold text-text-secondary uppercase tracking-wider text-center" colSpan={2}>L10</th>
                <th className="py-3 px-2 text-[10px] font-semibold text-text-secondary uppercase tracking-wider text-center" colSpan={2}>+/-</th>
                <th className="py-3 px-2 text-[10px] font-semibold text-text-secondary uppercase tracking-wider text-center">H2H</th>
                <th className="py-3 px-2 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {filteredGames.map((game) => {
                const isExpanded = expandedRows.has(game.game_id);
                const { home, away, sport } = game;

                // Determine if either team is hot/cold
                const hasHotTeam = home?.is_hot_streak || away?.is_hot_streak;
                const hasColdTeam = home?.is_cold_streak || away?.is_cold_streak;

                return (
                  <>
                    <tr
                      key={`${sport}-${game.game_id}`}
                      className={`transition-colors hover:bg-accent-blue/5 ${
                        hasHotTeam ? 'bg-success/5' : hasColdTeam ? 'bg-danger/5' : ''
                      }`}
                    >
                      {/* Matchup - AWAY @ HOME format */}
                      <td className="py-2 px-2">
                        <GameMatchupCell
                          away={away}
                          home={home}
                          gameTime={game.game_time}
                          sport={sport}
                          onGameClick={() => handleGameClick({ sport, game_id: game.game_id })}
                        />
                      </td>

                      {/* Away Streak */}
                      <td className="py-3 px-2 text-center border-l border-border/20">
                        <StreakBadge
                          streak={away?.current_streak}
                          streakDisplay={away?.streak_display}
                          isHot={away?.is_hot_streak}
                          isCold={away?.is_cold_streak}
                        />
                      </td>

                      {/* Home Streak */}
                      <td className="py-3 px-2 text-center">
                        <StreakBadge
                          streak={home?.current_streak}
                          streakDisplay={home?.streak_display}
                          isHot={home?.is_hot_streak}
                          isCold={home?.is_cold_streak}
                        />
                      </td>

                      {/* Away L10 */}
                      <td className="py-3 px-2 text-center border-l border-border/20">
                        <span className={`text-xs font-semibold ${
                          (away?.last_10_wins || 0) >= 7 ? 'text-success' :
                          (away?.last_10_wins || 0) <= 3 ? 'text-danger' : 'text-text-primary'
                        }`}>
                          {away?.last_10_display || '-'}
                        </span>
                      </td>

                      {/* Home L10 */}
                      <td className="py-3 px-2 text-center">
                        <span className={`text-xs font-semibold ${
                          (home?.last_10_wins || 0) >= 7 ? 'text-success' :
                          (home?.last_10_wins || 0) <= 3 ? 'text-danger' : 'text-text-primary'
                        }`}>
                          {home?.last_10_display || '-'}
                        </span>
                      </td>

                      {/* Away +/- */}
                      <td className="py-3 px-2 text-center border-l border-border/20">
                        <PointDiff diff={away?.point_diff || 0} />
                      </td>

                      {/* Home +/- */}
                      <td className="py-3 px-2 text-center">
                        <PointDiff diff={home?.point_diff || 0} />
                      </td>

                      {/* H2H from home team perspective */}
                      <td className="py-3 px-2 text-center border-l border-border/20">
                        <H2HDisplay
                          record={game.h2h_record}
                          lastResult={game.h2h_last_result}
                          lastDate={game.h2h_last_date}
                        />
                      </td>

                      {/* Expand - show lock for free users */}
                      <td className="py-3 px-2">
                        <button
                          onClick={() => toggleRowExpand(game.game_id)}
                          className="p-1 rounded hover:bg-bg-secondary transition-colors"
                          title="View detailed stats"
                        >
                          <ChevronDownIcon className={`w-4 h-4 text-text-secondary transition-transform ${
                            isExpanded ? 'rotate-180' : ''
                          }`} />
                        </button>
                      </td>
                    </tr>

                    {/* Expanded details row - shows both teams side by side */}
                    {isExpanded && (
                      <tr className="bg-bg-secondary/30">
                        <td colSpan={9} className="py-4 px-6">
                          <div className="grid grid-cols-2 gap-8">
                            {/* Away Team Details */}
                            <div className="border-r border-border/30 pr-6">
                              <h4 className="text-xs font-bold text-text-primary mb-3 flex items-center gap-2">
                                <MapPinIcon className="w-4 h-4 text-purple-400" />
                                {away?.team_abbreviation} (Away)
                              </h4>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <span className="text-[10px] text-text-secondary uppercase">Scoring</span>
                                  <div className="text-xs mt-1">
                                    <div className="flex justify-between"><span className="text-text-secondary">For:</span><span className="text-text-primary">{away?.avg_points_for}/g</span></div>
                                    <div className="flex justify-between"><span className="text-text-secondary">Against:</span><span className="text-text-primary">{away?.avg_points_against}/g</span></div>
                                  </div>
                                </div>
                                <div>
                                  <span className="text-[10px] text-text-secondary uppercase">Away Record</span>
                                  <div className="text-sm font-semibold text-text-primary mt-1">{away?.away_record}</div>
                                </div>
                              </div>
                            </div>

                            {/* Home Team Details */}
                            <div className="pl-2">
                              <h4 className="text-xs font-bold text-text-primary mb-3 flex items-center gap-2">
                                <HomeIcon className="w-4 h-4 text-emerald-400" />
                                {home?.team_abbreviation} (Home)
                              </h4>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <span className="text-[10px] text-text-secondary uppercase">Scoring</span>
                                  <div className="text-xs mt-1">
                                    <div className="flex justify-between"><span className="text-text-secondary">For:</span><span className="text-text-primary">{home?.avg_points_for}/g</span></div>
                                    <div className="flex justify-between"><span className="text-text-secondary">Against:</span><span className="text-text-primary">{home?.avg_points_against}/g</span></div>
                                  </div>
                                </div>
                                <div>
                                  <span className="text-[10px] text-text-secondary uppercase">Home Record</span>
                                  <div className="text-sm font-semibold text-text-primary mt-1">{home?.home_record}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
